import React, { FC, useEffect, useState } from 'react'

import AutoSelectAsyncList from '@pixel/core/AutoSelectAsyncList'

import useEmployeeAutocomplete, { Employee } from '../hooks/useEmployeeAutocomplete'
import { useLocalizedText } from '../contexts/Localization/localizationHooks';
import { LocalizedText } from '../contexts/Localization/LocalizationTypes';

interface UserAutoCompleteProps {
    id?: string
    labelLocale: LocalizedText
    value: Employee
    onChange: (event: any, newValue: Employee) => void
    optional?: boolean
    error?: boolean
}
export const UserAutoComplete: FC<UserAutoCompleteProps> = ({
    id,
    labelLocale,
    value,
    onChange,
    error = false,
    optional = false,
}) => {
    const localizedLabel = useLocalizedText(labelLocale)

    const [textInput, setTextInput] = useState(value?.fullName ?? '')
    const [page, setPage] = useState(0)
    const [options, setOptions] = useState<Array<Employee>>([])

    const {
        employees,
        employeesCount,
        loading,
    } = useEmployeeAutocomplete({
        page,
        keywords: textInput,
        onNewResults: (response) => {
            setOptions(response.employees)
        },
        onNextPageResults: (response) => {
            setOptions(currentOptions => [...currentOptions, ...response.employees])
        }
    })

    useEffect(() => {
        setTextInput(value?.fullName ?? '')
    }, [value])

    return (
        <AutoSelectAsyncList
            id={id}
            fieldLabel={localizedLabel}
            paginated
            multiple={false}
            debounceInput={2500}
            loading={loading}
            loadingNextPage={loading}
            noResults={options.length === 0}
            options={options}
            value={value}
            error={error}
            inputValue={textInput}
            disableCloseOnSelect={false}
            onDebouncedKeyStroke={(newInput: string) => {
                setPage(0)
                setTextInput(newInput)
            }}
            onChange={(event: any, newValue: Employee) => {
                if (typeof onChange === 'function') {
                    if (newValue) {
                        setTextInput(newValue.fullName)
                    } else {
                        setTextInput('')
                    }
                    onChange(event, newValue)
                }
            }}
            onFetchNextPage={() => setPage(current => current + 1)}
            currentPage={page}
            renderOption={(props, option: Employee) => (
                <div style={{ display: 'flex', alignItems: 'center' }} {...props}>
                    <div style={{ marginRight: '16px' }}>
                        <img src={option.userProfilePhotoUrl} width={35} height={35} style={{ borderRadius: '50%' }} />
                    </div>
                    <div>
                        <p style={{ margin: 0, fontWeight: 600 }}>{option.fullName}</p>
                        <p style={{ margin: 0 }}>{option.email}</p>
                    </div>
                </div>
            )}

            getOptionLabel={(option: Employee) => option.fullName}
            getValueLabel={(option: Employee) => option.fullName}
            isOptionEqualToValue={(option: Employee, value: Employee) => option.EId === value.EId}
        />
    )
}

export default UserAutoComplete
